import { burnModalCopy, BURN_NFT } from './BurnNftModal.tsx';
import { type DELETE_DRAFT } from './DeleteDraftModal.tsx';
import { type BurnNftStatus } from '../../hooks/useBurnNft.ts';
import { type DeleteDraftStatus } from '../../hooks/useDeleteDraft.ts';
import { TRANSFER_TOKEN, transferModalCopy } from './TransferTokenModal.tsx';
import { type TransferTokenStatus } from '../../hooks/useTransferToken.ts';
import { type RefreshMetadataStatus } from '../../hooks/useRefreshMetadata.ts';
import { defineMessages } from 'dibs-react-intl';

export const MINT_NFT = 'MINT_NFT';
export const mintModalCopy = defineMessages({
    mintCta: { defaultMessage: 'Mint', id: 'nft.create.mintDraft.mintCta' },
    auctionCta: { defaultMessage: 'Create Auction', id: 'nft.create.mintDraft.auctionCta' },
    initialTitle: { defaultMessage: 'Mint NFT', id: 'nft.create.mintDraft.initialTitle' },
    initial: {
        id: 'nft.create.mintDraft.initial',
        defaultMessage:
            "Please note before minting this NFT that this action cannot be undone. Please check that all information is correct and that you're happy with the preview. <strong>Once you mint the NFT, you will not be able to edit this information.</strong> Gas fees apply.",
    },
    initialFooter: {
        id: 'nft.create.mintDraft.initialFooter',
        defaultMessage:
            "By clicking mint above, you accept 1stDibs' <a>User Agreement</a>, {termsOfUse}, and assert that this is an original artwork that does not contain copyrighted or restricted material. You also assert that this work has not been and will not be tokenized or offered for digital purchase elsewhere.",
    },
    pinningTitle: {
        defaultMessage: 'Pinning to IPFS',
        id: 'nft.create.mintDraft.pinningTitle',
    },
    pinning: {
        id: 'nft.create.mintDraft.pinning',
        defaultMessage:
            "One moment while we pin the NFT's media and metadata to IPFS. This helps to ensure that your NFT will always be available. Your wallet will launch as soon as this process completes.",
    },
    mintingTitle: { defaultMessage: 'Minting NFT', id: 'nft.create.mintDraft.mintingTitle' },
    minting: {
        id: 'nft.create.mintDraft.minting',
        defaultMessage:
            'Your NFT is being minted to the Ethereum blockchain! This may take a few minutes. You do not need to leave this page open for the process to complete successfully.',
    },
    mintingToast: {
        id: 'nft.create.mintDraft.mintingToast',
        defaultMessage:
            'Your NFT is being minted to the Ethereum blockchain! This may take a few minutes.',
    },
    successTitle: {
        defaultMessage: 'Your NFT was minted',
        id: 'nft.create.mintDraft.successTitle',
    },
    success: {
        defaultMessage:
            'Congrats! Your NFT is now on the Ethereum blockchain! The next step is to list it on 1stDibs.',
        id: 'nft.create.mintDraft.success',
    },
    successToast: {
        id: 'nft.create.mintDraft.successToast',
        defaultMessage: 'Congrats! Your NFT is now on the Ethereum blockchain!',
    },
    errorTitle: {
        defaultMessage: 'Your NFT was not minted',
        id: 'nft.create.mintDraft.errorTitle',
    },
    error: {
        defaultMessage: 'There was an error minting your NFT.',
        id: 'nft.create.mintDraft.error',
    },
});
export type MintNftStatus = null | 'pinning' | 'minting' | 'success' | 'error';

export type CancelStatus = null | 'inflight' | 'success';
export const CANCEL_AUCTION = 'CANCEL_AUCTION';
export const cancelModalCopy = defineMessages({
    initialTitle: { id: 'nft.create.cancelAuction.title', defaultMessage: 'Cancel Auction' },
    inflightTitle: {
        id: 'nft.create.cancelAuction.inflightTItle',
        defaultMessage: 'Canceling Auction',
    },
    successTitle: {
        id: 'nft.create.cancelAuction.successTitle',
        defaultMessage: 'Auction Canceled',
    },
    initial: {
        id: 'nft.create.cancelAuction.body',
        defaultMessage:
            'Click the button below to cancel the auction currently live for this NFT. You can choose to create a new auction in the future. You will be charged a gas fee for canceling.',
    },
    cta: { id: 'nft.create.cancelAuction.cta', defaultMessage: 'Cancel Auction' },
    inflight: {
        id: 'nft.create.cancelAuction.body.inflight',
        defaultMessage:
            'We are canceling your auction. You can choose to create a new auction in the future. This may take a few minutes. You do not need to leave this page open for the process to complete successfully.',
    },
    inflightToast: {
        id: 'nft.create.cancelAuction.body.inflightToast',
        defaultMessage: 'We are canceling your auction. This may take a few minutes.',
    },
    success: {
        id: 'nft.create.cancelAuction.body.success',
        defaultMessage:
            'Your auction on this item was canceled successfully. You can choose to create a new auction in the future. Gas fees apply to all auctions created.',
    },
    successToast: {
        id: 'nft.create.cancelAuction.body.successToast',
        defaultMessage: 'Your auction was canceled successfully.',
    },
    contactSupportBody: {
        id: 'nft.create.cancelAuction.body.contactSupport',
        defaultMessage: 'To cancel this auction, please contact 1stDibs support at {supportLink}.',
    },
    contactSupportCta: {
        id: 'nft.create.cancelAuction.cta.contactSupport',
        defaultMessage: 'Contact Support',
    },
});

export const REFRESH_METADATA = 'REFRESH_METADATA';
export const refreshMetadataCopy = defineMessages({
    inflightTitle: {
        id: 'nft.create.refreshMetadat.inflightTItle',
        defaultMessage: 'Refreshing metadata',
    },
    successTitle: {
        id: 'nft.create.refreshMetadat.successTitle',
        defaultMessage: 'Metadata refreshed',
    },
    inflightToast: {
        id: 'nft.create.refreshMetadat.body.inflightToast',
        defaultMessage: 'We are in the process of updating this NFT.',
    },
    successToast: {
        id: 'nft.create.refreshMetadat.body.successToast',
        defaultMessage: 'Your NFT was updated.',
    },
});

export const SPLIT_NFT = 'SPLIT_NFT';

export type ModalStatus =
    | MintNftStatus
    | BurnNftStatus
    | CancelStatus
    | DeleteDraftStatus
    | TransferTokenStatus;

export type ModalAction =
    | typeof DELETE_DRAFT
    | typeof CANCEL_AUCTION
    | typeof BURN_NFT
    | typeof MINT_NFT
    | typeof TRANSFER_TOKEN
    | typeof SPLIT_NFT;

export const MANAGE_NFT = 'MANAGE_NFT';
export const PREVIEW_DRAFT = 'PREVIEW_DRAFT';
export const GRANT_APPROVAL = 'GRANT_APPROVAL';
export const CANCEL_AUCTION_SECONDARY = 'CANCEL_AUCTION_SECONDARY';
export type TooltipAction =
    | typeof DELETE_DRAFT
    | typeof BURN_NFT
    | typeof TRANSFER_TOKEN
    | typeof MANAGE_NFT
    | typeof PREVIEW_DRAFT
    | typeof GRANT_APPROVAL
    | typeof CANCEL_AUCTION_SECONDARY
    | typeof REFRESH_METADATA;

export type SecondaryModalAction = typeof GRANT_APPROVAL | typeof CANCEL_AUCTION_SECONDARY;

export type ToastAction =
    | typeof CANCEL_AUCTION
    | typeof BURN_NFT
    | typeof MINT_NFT
    | typeof REFRESH_METADATA
    | typeof TRANSFER_TOKEN;
export type ToastStatus = NonNullable<
    MintNftStatus | BurnNftStatus | CancelStatus | RefreshMetadataStatus | TransferTokenStatus
>;

// intersect MessageDescriptor records (return type from defineMessages) to
// allow access with keys from any record in the intersection by making all
// properties optional.
type IntersectCopy = Partial<
    typeof burnModalCopy &
        typeof mintModalCopy &
        typeof cancelModalCopy &
        typeof refreshMetadataCopy &
        typeof transferModalCopy
>;

// allow status keys suffixed with `Toast`, even if undefined
type ToastCopy = {
    [K in `${ToastStatus}Toast`]?: K extends keyof IntersectCopy ? IntersectCopy[K] : undefined;
};

export const copyMap: Record<ToastAction, IntersectCopy & ToastCopy> = {
    [MINT_NFT]: mintModalCopy,
    [BURN_NFT]: burnModalCopy,
    [CANCEL_AUCTION]: cancelModalCopy,
    [REFRESH_METADATA]: refreshMetadataCopy,
    [TRANSFER_TOKEN]: transferModalCopy,
};
